import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faSpinner, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCrown, faMedal } from '@fortawesome/pro-solid-svg-icons';
import { getConciergeLeaderboard, getConciergeEditorClaims, deleteConciergeClaim } from '../../api/noir';
import { getRootSMSUrl, getSmartImage } from '../../helpers/helpers';
import { getPrettyNumber, getPrettyPriceDisplay } from '../../helpers/formatting';
import Tooltip from '../General/Tooltip';
import './NoirLeaderboard.scss';

const formatDuration = ms => {
  if (!ms || ms < 0) return '-';
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

const COLUMNS = [
  { key: 'rank', label: '#', sortable: false },
  { key: 'name', label: 'Editor', sortable: false },
  { key: 'conciergeEditorScore', label: 'Score', sortable: true },
  { key: 'conciergeAvgNoirRating', label: 'Avg Grade', sortable: true },
  { key: 'conciergeEditorPercentile', label: 'Percentile', sortable: true },
  { key: 'conciergeClaimsCompleted', label: 'Completed', sortable: true },
  { key: 'conciergeClaimsTotal', label: 'Total', sortable: true },
  { key: 'conciergeClaimsAbandoned', label: 'Abandoned', sortable: true },
  { key: 'conciergeAvgCompletionTimeMinutes', label: 'Avg Time', sortable: true },
  { key: 'conciergeClickRate', label: 'CTR', sortable: true },
  { key: 'conciergePurchaseRate', label: 'CVR', sortable: true },
  { key: 'conciergeVolume', label: 'Volume', sortable: true },
  { key: 'conciergeCommission', label: 'Commission', sortable: true },
  { key: 'conciergeItemsOrdered', label: 'Items Ordered', sortable: true }
];

const GRADE_TIERS = [
  { min: 9, tierClass: 'highest', label: 'World-Class Response' },
  { min: 8, tierClass: 'higher', label: 'Excellent Work' },
  { min: 7, tierClass: 'high', label: 'Solid Work' },
  { min: 6, tierClass: 'mid', label: 'Needs Improvement' },
  { min: 5, tierClass: 'low', label: 'Below Expectations' },
  { min: 3, tierClass: 'lower', label: 'Significant Issues' },
  { min: -Infinity, tierClass: 'lowest', label: 'Unacceptable' }
];

const getGradeTier = rating => {
  const tier = GRADE_TIERS.find(t => rating >= t.min) || GRADE_TIERS[GRADE_TIERS.length - 1];
  return { tierClass: tier.tierClass, label: tier.label };
};

const formatCellValue = (key, value) => {
  if (value == null) return '-';
  switch (key) {
    case 'conciergeAvgNoirRating':
    case 'conciergeEditorScore':
      return Number(value).toFixed(1);
    case 'conciergeAvgCompletionTimeMinutes':
      return `${Math.round(value)}m`;
    case 'conciergeClickRate':
    case 'conciergePurchaseRate':
      return `${(value * 100).toFixed(1)}%`;
    case 'conciergeVolume':
    case 'conciergeCommission':
      return getPrettyPriceDisplay(value);
    case 'conciergeItemsOrdered':
      return getPrettyNumber(value);
    case 'conciergeEditorPercentile':
      return `Top ${Math.max(1, Math.round(100 - value))}%`;
    default:
      return value;
  }
};

const getRankDisplay = rank => {
  if (rank === 1) return <FontAwesomeIcon icon={faCrown} className='rank-icon gold' />;
  if (rank === 2) return <FontAwesomeIcon icon={faMedal} className='rank-icon silver' />;
  if (rank === 3) return <FontAwesomeIcon icon={faMedal} className='rank-icon bronze' />;
  return <span className='rank-number'>{rank}</span>;
};

const NoirLeaderboard = () => {
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('conciergeEditorScore');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedEditorId, setExpandedEditorId] = useState(null);
  const [editorClaims, setEditorClaims] = useState({});
  const [loadingClaims, setLoadingClaims] = useState(null);

  const fetchLeaderboard = useCallback(() => {
    setLoading(true);
    getConciergeLeaderboard({ sortBy, sortDirection })
      .then(data => setEditors(data.editors || []))
      .catch(() => window.ALERT?.error?.('Failed to load leaderboard.'))
      .finally(() => setLoading(false));
  }, [sortBy, sortDirection]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleSort = columnKey => {
    if (sortBy === columnKey) {
      setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(columnKey);
      setSortDirection('desc');
    }
  };

  const handleEditorClick = async editorId => {
    if (expandedEditorId === editorId) {
      setExpandedEditorId(null);
      return;
    }

    setExpandedEditorId(editorId);

    if (!editorClaims[editorId]) {
      setLoadingClaims(editorId);
      try {
        const data = await getConciergeEditorClaims(editorId);
        setEditorClaims(prev => ({ ...prev, [editorId]: data.claims || [] }));
      } catch {
        window.ALERT?.error?.('Failed to load editor claims.');
      } finally {
        setLoadingClaims(null);
      }
    }
  };

  const handleDeleteClaim = (claim, editorId) => {
    confirmAlert({
      title: 'Delete Request & Claim',
      message: `This will permanently delete the entire ConciergeRequest (${claim.requestExternalId ||
        'unknown'}), ALL associated claims, the conversation thread, and all messages. This cannot be undone.`,
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Delete Everything',
          onClick: async () => {
            try {
              await deleteConciergeClaim(claim.id);
              setEditorClaims(prev => ({
                ...prev,
                [editorId]: (prev[editorId] || []).filter(c => c.id !== claim.id)
              }));
              window.ALERT?.success?.('Request and all associated data deleted.');
            } catch {
              window.ALERT?.error?.('Failed to delete request.');
            }
          }
        }
      ]
    });
  };

  const renderSortIcon = columnKey => {
    if (sortBy !== columnKey) return null;
    return <FontAwesomeIcon icon={sortDirection === 'desc' ? faChevronDown : faChevronUp} className='sort-icon' />;
  };

  const renderCell = (col, editor, rank) => {
    if (col.key === 'rank') return getRankDisplay(rank);

    if (col.key === 'name') {
      return (
        <div className='editor-identity'>
          {editor.image ? <img src={getSmartImage(editor.image)} alt='' className='editor-avatar' /> : <div className='editor-avatar placeholder' />}
          <span className='editor-name'>{editor.name || editor.username || `Editor #${editor.id}`}</span>
        </div>
      );
    }

    if (col.key === 'conciergeAvgNoirRating' && editor.conciergeAvgNoirRating != null) {
      return (
        <span className={cn('grade-value', getGradeTier(editor.conciergeAvgNoirRating).tierClass)}>
          {formatCellValue(col.key, editor.conciergeAvgNoirRating)}
        </span>
      );
    }

    if (col.key === 'conciergeEditorScore' && editor.conciergeEditorScore != null) {
      return <span className='score-value'>{formatCellValue(col.key, editor.conciergeEditorScore)}</span>;
    }

    return formatCellValue(col.key, editor[col.key]);
  };

  const renderEditorClaims = editorId => {
    if (loadingClaims === editorId) {
      return (
        <div className='claims-loading'>
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading claims...</span>
        </div>
      );
    }

    const claims = editorClaims[editorId];
    if (!claims || claims.length === 0) return <div className='claims-empty'>No graded claims yet.</div>;

    return (
      <div className='claims-panel'>
        <div className='claims-grid header'>
          <span>Date</span>
          <span>Shopper</span>
          <span>Brief</span>
          <span>Grade</span>
          <span>Duration</span>
          <span>Picks</span>
          <span>Ordered</span>
          <span>Commission</span>
          <span>Products</span>
          <span>Reasoning</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {claims.map(claim => (
          <div
            key={claim.id}
            className='claims-grid row'
            onClick={e => {
              e.stopPropagation();
              if (claim.requestExternalId) {
                const noirUrl = `${getRootSMSUrl()}/noir/${claim.requestExternalId}`;
                window.open(noirUrl, '_blank');
              }
            }}
          >
            <span className='claim-date'>
              {claim.submittedAt ? new Date(claim.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
            </span>
            <span className='claim-shopper'>{claim.shopperName || 'Unknown'}</span>
            <span className='claim-brief'>
              {claim.briefPreview ? (
                <Tooltip message={claim.briefPreview} position='top' interactive>
                  <span className='brief-text'>{claim.briefPreview}</span>
                </Tooltip>
              ) : (
                '-'
              )}
            </span>
            <span className='claim-grade-cell'>
              {claim.noirRating != null ? (
                <span className={cn('claim-grade', getGradeTier(claim.noirRating).tierClass)}>{Number(claim.noirRating).toFixed(1)}</span>
              ) : (
                <span className='claim-grade ungraded'>-</span>
              )}
            </span>
            <span className='claim-duration'>
              {claim.createdAt && claim.submittedAt ? formatDuration(new Date(claim.submittedAt) - new Date(claim.createdAt)) : '-'}
            </span>
            <span className='claim-picks'>{claim.productCount || 0}</span>
            <span className='claim-ordered'>{claim.itemsOrdered || 0}</span>
            <span className='claim-commission'>{claim.commissionEarned > 0 ? getPrettyPriceDisplay(claim.commissionEarned) : '-'}</span>
            <span className='claim-products'>
              {claim.products?.length > 0 ? (
                <div className='product-thumbs'>
                  {claim.products.map((product, i) => {
                    const imgUrl = getSmartImage(product.image);
                    if (!imgUrl) return null;
                    return (
                      <Tooltip key={i} message={[product.title, product.brand].filter(Boolean).join(' — ')} position='top'>
                        <img src={imgUrl} alt='' className='product-thumb' />
                      </Tooltip>
                    );
                  })}
                </div>
              ) : (
                '-'
              )}
            </span>
            <span className='claim-reasoning'>
              {claim.noirRatingReasoning ? (
                <Tooltip message={claim.noirRatingReasoning} position='top' interactive>
                  <span className='reasoning-text'>{claim.noirRatingReasoning}</span>
                </Tooltip>
              ) : (
                '-'
              )}
            </span>
            <span>
              <span className={cn('status-badge', claim.status)}>{claim.status}</span>
            </span>
            <span className='claim-actions'>
              {claim.collectionId && (
                <button
                  type='button'
                  className='action-view-collection'
                  onClick={e => {
                    e.stopPropagation();
                    const collectionUrl = `${getRootSMSUrl()}/collections/${claim.collectionId}`;
                    window.open(collectionUrl, '_blank');
                  }}
                >
                  View Collection
                </button>
              )}
              <button
                type='button'
                className='action-delete'
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteClaim(claim, editorId);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className='noir-leaderboard'>
        <div className='leaderboard-placeholder'>
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading leaderboard...</span>
        </div>
      </div>
    );
  }

  if (editors.length === 0) {
    return (
      <div className='noir-leaderboard'>
        <div className='leaderboard-placeholder'>No editor data available yet.</div>
      </div>
    );
  }

  return (
    <div className='noir-leaderboard'>
      <div className='leaderboard-grid header'>
        {COLUMNS.map(col => (
          <div
            key={col.key}
            className={cn('th', col.key, { sortable: col.sortable, active: sortBy === col.key })}
            onClick={col.sortable ? () => handleSort(col.key) : undefined}
          >
            <span>{col.label}</span>
            {col.sortable && renderSortIcon(col.key)}
          </div>
        ))}
      </div>

      {editors.map((editor, index) => {
        const rank = index + 1;
        const isExpanded = expandedEditorId === editor.id;

        return (
          <div key={editor.id} className={cn('editor-entry', { expanded: isExpanded, 'top-three': rank <= 3 })}>
            <div className={cn('leaderboard-grid row', { expanded: isExpanded })} onClick={() => handleEditorClick(editor.id)}>
              {COLUMNS.map(col => (
                <div key={col.key} className={cn('td', col.key)}>
                  {renderCell(col, editor, rank)}
                </div>
              ))}
            </div>
            {isExpanded && <div className='editor-detail'>{renderEditorClaims(editor.id)}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default NoirLeaderboard;
